import { z } from 'zod';

import {
  createTaskObjectSchema,
  type CreateTaskInput,
  type GuidelineFile,
} from '@/lib/validations/task';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

// All task-mirror fields are optional in template mode: a template can be
// saved partially filled. The full createTaskSchema (with the cross-field
// `.superRefine` for "Other" product type) re-validates at task submit time,
// so partial templates can never produce an invalid task.
export const createTaskTemplateSchema = createTaskObjectSchema
  .partial()
  .extend({
    name: z
      .string()
      .trim()
      .min(1, 'Template name is required')
      .max(80, 'Template name must be 80 characters or fewer'),
    deadline_offset_days: z
      .number()
      .int()
      .min(0, 'Offset must be 0 or more days')
      .max(365, 'Offset cannot exceed 365 days')
      .nullable()
      .optional(),
  });

export type CreateTaskTemplateInput = z.infer<typeof createTaskTemplateSchema>;

export const updateTaskTemplateSchema = createTaskTemplateSchema.partial();
export type UpdateTaskTemplateInput = z.infer<typeof updateTaskTemplateSchema>;

// ---------------------------------------------------------------------------
// Prefill helpers
// ---------------------------------------------------------------------------

/** Stored shape returned by listTaskTemplates / getTaskTemplate. */
export interface TaskTemplateRow {
  id: string;
  workspace_id: string;
  created_by: string;
  name: string;
  title: string | null;
  description: unknown | null;
  category: string | null;
  content_type: string | null;
  product_category: string | null;
  product_type_other: string | null;
  word_count_range: string | null;
  research_required: boolean | null;
  brand_voice: string | null;
  guidelines_url: string | null;
  guidelines_files: GuidelineFile[] | null;
  required_experience_level: string | null;
  language: string | null;
  amount: number | null;
  assigned_writer_id: string | null;
  deadline_offset_days: number | null;
  created_at: string;
  updated_at: string;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatMMDDYYYY(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Build the task form's `initialValues` from a stored template row. The
 * caller passes the result straight to `<TaskForm initialValues={...} />`,
 * which mirrors the existing prefill path used by the writers-page CTA.
 *
 * Tiptap JSON for `description` is passed through verbatim (the simple
 * editor accepts either a JSON object or a string). The PDF guidelines URL
 * is reused as-is — both the source template and the resulting task point
 * at the same Storage object.
 */
export function templateToInitialValues(
  template: TaskTemplateRow,
  now: Date = new Date(),
): Partial<CreateTaskInput> {
  const deadline =
    typeof template.deadline_offset_days === 'number'
      ? formatMMDDYYYY(addDays(now, template.deadline_offset_days))
      : '';

  // The form expects `description` as a string (the SimpleEditor wraps a
  // controlled value). We persist Tiptap content as JSONB and stringify on
  // hand-off. The form's `useForm({ defaultValues })` accepts whatever shape
  // the existing flow gives it.
  let description: string | undefined;
  if (template.description == null) {
    description = '';
  } else if (typeof template.description === 'string') {
    description = template.description;
  } else {
    description = JSON.stringify(template.description);
  }

  return {
    title: template.title ?? '',
    category: (template.category ?? undefined) as CreateTaskInput['category'] | undefined,
    content_type: (template.content_type ?? undefined) as
      | CreateTaskInput['content_type']
      | undefined,
    product_category: (template.product_category ?? undefined) as
      | CreateTaskInput['product_category']
      | undefined,
    product_type_other: template.product_type_other ?? '',
    word_count_range: (template.word_count_range ?? undefined) as
      | CreateTaskInput['word_count_range']
      | undefined,
    research_required: template.research_required ?? false,
    brand_voice: (template.brand_voice ?? undefined) as
      | CreateTaskInput['brand_voice']
      | undefined,
    guidelines_url: template.guidelines_url ?? '',
    guidelines_files: template.guidelines_files ?? [],
    description,
    required_experience_level: (template.required_experience_level ?? undefined) as
      | CreateTaskInput['required_experience_level']
      | undefined,
    language: (template.language ?? 'English - American') as CreateTaskInput['language'],
    amount: template.amount ?? undefined,
    deadline,
    assigned_writer_id: template.assigned_writer_id ?? undefined,
  };
}
